/**
 * @license
 * Copyright 2021 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
/* eslint no-param-reassign: 0 */
import * as posedetection from '@tensorflow-models/pose-detection';
import * as faceLandmarksDetection from '@tensorflow-models/face-landmarks-detection';
import * as params from './params';
import { isMobile } from './util';
import { TRIANGULATION } from './triangulation';

// These anchor points allow the hand pointcloud to resize according to its
// position in the input.
const ANCHOR_POINTS = [[0, 0, 0], [0, 0.1, 0], [-0.1, 0, 0], [-0.1, -0.1, 0]];
const ANCHOR_POINTS_POSES = [[0, 0, 0], [0, 1, 0], [-1, 0, 0], [-1, -1, 0]];

const COLOR_PALETTE = [
  '#ffffff', '#800000', '#469990', '#e6194b', '#42d4f4', '#fabed4', '#aaffc3',
  '#9a6324', '#000075', '#f58231', '#4363d8', '#ffd8b1', '#dcbeff', '#808000',
  '#ffe119', '#911eb4', '#bfef45', '#f032e6', '#3cb44b', '#a9a9a9',
];

export const LABEL_TO_COLOR = {
  lips: '#E0E0E0',
  leftEye: '#30FF30',
  leftEyebrow: '#30FF30',
  leftIris: '#30FF30',
  rightEye: '#FF3030',
  rightEyebrow: '#FF3030',
  rightIris: '#FF3030',
  faceOval: '#E0E0E0',
};

const fingerLookupIndices = {
  thumb: [0, 1, 2, 3, 4],
  indexFinger: [0, 5, 6, 7, 8],
  middleFinger: [0, 9, 10, 11, 12],
  ringFinger: [0, 13, 14, 15, 16],
  pinky: [0, 17, 18, 19, 20],
}; // for rendering each finger as a polyline

const connections = [
  [0, 1], [1, 2], [2, 3], [3, 4],
  [0, 5], [5, 6], [6, 7], [7, 8],
  [0, 9], [9, 10], [10, 11], [11, 12],
  [0, 13], [13, 14], [14, 15], [15, 16],
  [0, 17], [17, 18], [18, 19], [19, 20],
];

// const NUM_KEYPOINTS = 6;
const NUM_KEYPOINTS = 478;
const NUM_IRIS_KEYPOINTS = 5;
const GREEN = '#32EEDB';
const RED = '#FF2C35';
const BLUE = '#157AB3';

export class Camera {
  constructor(video, canvas) {
    // this.video = document.getElementById('video');
    // this.canvas = document.getElementById('output');
    // this.ctx = this.canvas.getContext('2d');
    this.preview = false;
  }

  /**
   * Initiate a Camera instance and wait for the camera stream to be ready.
   * @param cameraParam From app `STATE.camera`.
   */
  static async setupCamera(cameraParam, wrapper = 'canvas-wrapper') {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('Browser API navigator.mediaDevices.getUserMedia not available');
    }

    const { targetFPS, sizeOption } = cameraParam;
    const $size = params.VIDEO_SIZE[sizeOption];
    const videoConfig = {
      audio: false,
      video: {
        facingMode: 'user',
        // Only setting the video to a specified size for large screen, on
        // mobile devices accept the default size.
        width: isMobile() ? params.VIDEO_SIZE['360 X 270'].width : $size.width,
        height: isMobile() ? params.VIDEO_SIZE['360 X 270'].height : $size.height,
        frameRate: {
          ideal: targetFPS,
        },
      },
    };

    const stream = await navigator.mediaDevices.getUserMedia(videoConfig);

    const camera = new Camera();
    const canvasContainer = document.querySelector(`#${wrapper}`);
    if (canvasContainer) {
      camera.video = canvasContainer.getElementsByTagName('video')[0];
      camera.canvas = canvasContainer.getElementsByTagName('canvas')[0];
      camera.ctx = camera.canvas.getContext('2d');

      camera.video.srcObject = stream;

      await new Promise((resolve) => {
        camera.video.onloadedmetadata = () => {
          resolve(this.video);
        };
      });

      camera.video.play();

      const videoWidth = camera.video.videoWidth;
      const videoHeight = camera.video.videoHeight;
      // Must set below two lines, otherwise video element doesn't show.
      camera.video.width = videoWidth;
      camera.video.height = videoHeight;

      camera.canvas.width = videoWidth;
      camera.canvas.height = videoHeight;
      canvasContainer.style = `width: ${videoWidth}px; height: ${videoHeight}px`;

      // Because the image from camera is mirrored, need to flip horizontally.
      camera.ctx.translate(camera.video.videoWidth, 0);
      camera.ctx.scale(-1, 1);
    } else {
      return null;
    }

    return camera;
  }

  drawRectangle(x0, y0, x1, y1, label = '', color = '#FF0000', lineWidth = '2', targetMark = true) {
    this.ctx.beginPath();
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = lineWidth;

    if (targetMark) {
      const cornerLength = (x1 - x0) / 3;
      // Top-left corner
      this.ctx.moveTo(x0, y0);
      this.ctx.lineTo(x0 + cornerLength, y0);
      this.ctx.moveTo(x0, y0);
      this.ctx.lineTo(x0, y0 + cornerLength);

      // Top-right corner
      this.ctx.moveTo(x1, y0);
      this.ctx.lineTo(x1 - cornerLength, y0);
      this.ctx.moveTo(x1, y0);
      this.ctx.lineTo(x1, y0 + cornerLength);

      // Bottom-left corner
      this.ctx.moveTo(x0, y1);
      this.ctx.lineTo(x0 + cornerLength, y1);
      this.ctx.moveTo(x0, y1);
      this.ctx.lineTo(x0, y1 - cornerLength);

      // Bottom-right corner
      this.ctx.moveTo(x1, y1);
      this.ctx.lineTo(x1 - cornerLength, y1);
      this.ctx.moveTo(x1, y1);
      this.ctx.lineTo(x1, y1 - cornerLength);
    } else {
      this.ctx.rect(x0, y0, x1 - x0, y1 - y0);
    }

    this.ctx.stroke();

    if (label) {
      const padding = 7;
      const textShadow = 1.3;
      const textHeight = 20; // Approximate text height
      this.ctx.font = `${textHeight}px Arial`;
      const textWidth = this.ctx.measureText(label).width + (padding * 2);

      this.ctx.fillStyle = color;
      const fillRectHeight = textHeight + (padding * 2);
      const fillRectWidth = textWidth;
      this.ctx.fillRect(x1, y0, -textWidth, textHeight + (padding * 2));

      this.ctx.save();
      this.ctx.scale(-1, 1);
      this.ctx.translate(-textWidth, 0);

      this.ctx.fillStyle = '#FFFFFF9f';
      this.ctx.fillText(label, -x1 + textWidth + padding + textShadow, y0 + textHeight + (padding * 0.6) + textShadow);
      this.ctx.fillStyle = '#000';
      this.ctx.fillText(label, -x1 + textWidth + padding, y0 + textHeight + padding - 2);

      this.ctx.restore();
    }
  }

  async drawPreview(image) {
    console.log('==============drawPreview', this.video.videoWidth, this.video.videoHeight, image);
    await new Promise((resolve) => {
      this.preview = true;
      this.ctx.drawImage(image, 0, 0, this.video.videoWidth, this.video.videoHeight);
      setTimeout(() => {
        this.preview = false;
        resolve();
      }, 2000);
    });
  }

  snapshot() {
    return this.canvas.toDataURL('image/png')
  }

  stop() {
    this.video.pause()
    if (this.video.srcObject instanceof MediaStream) {
      this.video.srcObject.getTracks().forEach((track) => track.stop())
    }
  }

  drawCtx() {
    if (this.preview) return;
    this.ctx.drawImage(this.video, 0, 0, this.video.videoWidth, this.video.videoHeight);
  }

  clearCtx() {
    if (this.preview) return;
    this.ctx.clearRect(0, 0, this.video.videoWidth, this.video.videoHeight);
  }

  /**
   * Draw the keypoints on the video.
   * @param hands A list of hands to render.
   */
  drawResults(hands) {
    // Sort by right to left hands.
    hands.sort((hand1, hand2) => {
      if (hand1.handedness < hand2.handedness) return 1;
      if (hand1.handedness > hand2.handedness) return -1;
      return 0;
    });

    // Pad hands to clear empty scatter GL plots.
    while (hands.length < 2) hands.push({});

    for (let i = 0; i < hands.length; i += 1) {
      // Third hand and onwards scatterGL context is set to null since we
      // don't render them.
      const ctxt = null;
      this.drawResult(hands[i], ctxt);
    }
  }

  /**
   * Draw the keypoints on the video.
   * @param hand A hand with keypoints to render.
   * @param ctxt Scatter GL context to render 3D keypoints to.
   */
  drawResult(hand, ctxt) {
    if (hand.keypoints != null) {
      this.drawKeypoints(hand.keypoints, hand.handedness);
    }
  }

  /**
   * Draw the keypoints on the video.
   * @param keypoints A list of keypoints.
   * @param handedness Label of hand (either Left or Right).
   */
  drawKeypoints(keypoints, handedness) {
    const keypointsArray = keypoints;
    this.ctx.fillStyle = handedness === 'Left' ? 'Red' : 'Blue';
    this.ctx.strokeStyle = 'White';
    this.ctx.lineWidth = params.DEFAULT_LINE_WIDTH;

    for (let i = 0; i < keypointsArray.length; i += 1) {
      const y = keypointsArray[i].x;
      const x = keypointsArray[i].y;
      this.drawPoint(x - 2, y - 2, 3);
    }

    const fingers = Object.keys(fingerLookupIndices);
    for (let i = 0; i < fingers.length; i += 1) {
      const finger = fingers[i];
      const points = fingerLookupIndices[finger].map(idx => keypoints[idx]);
      this.drawPath(points, false);
    }
  }

  drawPath(points, closePath) {
    const region = new Path2D();
    region.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i += 1) {
      const point = points[i];
      region.lineTo(point.x, point.y);
    }

    if (closePath) {
      region.closePath();
    }
    this.ctx.stroke(region);
  }

  drawPoint(y, x, r) {
    this.ctx.beginPath();
    this.ctx.arc(x, y, r, 0, 2 * Math.PI);
    this.ctx.fill();
  }

  drawKeypointPose(keypoint, state) {
    // If score is null, just show the keypoint.
    const score = keypoint.score != null ? keypoint.score : 1;
    const scoreThreshold = state.modelConfig.scoreThreshold || 0;

    if (score >= scoreThreshold) {
      const circle = new Path2D();
      circle.arc(keypoint.x, keypoint.y, params.DEFAULT_RADIUS, 0, 2 * Math.PI);
      this.ctx.fill(circle);
      this.ctx.stroke(circle);
    }
  }

  /**
   * Draw the keypoints on the video.
   * @param keypoints A list of keypoints.
   */
  drawKeypointsPose(keypoints, state) {
    const keypointInd = posedetection.util.getKeypointIndexBySide(state.model);
    this.ctx.fillStyle = 'Red';
    this.ctx.strokeStyle = 'White';
    this.ctx.lineWidth = params.DEFAULT_LINE_WIDTH;

    keypointInd.middle.forEach((i) => {
      this.drawKeypointPose(keypoints[i], state);
    });

    this.ctx.fillStyle = 'Green';
    keypointInd.left.forEach((i) => {
      this.drawKeypointPose(keypoints[i], state);
    });

    this.ctx.fillStyle = 'Orange';
    keypointInd.right.forEach((i) => {
      this.drawKeypointPose(keypoints[i], state);
    });
  }

  /**
     * Draw the skeleton of a body on the video.
     * @param keypoints A list of keypoints.
     */
  drawSkeleton(keypoints, poseId, state) {
    // Each poseId is mapped to a color in the color palette.
    const color = state.modelConfig.enableTracking && poseId != null ? COLOR_PALETTE[poseId % 20] : 'White';
    this.ctx.fillStyle = color;
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = params.DEFAULT_LINE_WIDTH;

    posedetection.util.getAdjacentPairs(state.model).forEach(([i, j]) => {
      const kp1 = keypoints[i];
      const kp2 = keypoints[j];

      // If score is null, just show the keypoint.
      const score1 = kp1.score != null ? kp1.score : 1;
      const score2 = kp2.score != null ? kp2.score : 1;
      const scoreThreshold = state.modelConfig.scoreThreshold || 0;

      if (score1 >= scoreThreshold && score2 >= scoreThreshold) {
        this.ctx.beginPath();
        this.ctx.moveTo(kp1.x, kp1.y);
        this.ctx.lineTo(kp2.x, kp2.y);
        this.ctx.stroke();
      }
    });
  }

  /**
   * Draw the keypoints on the video.
   * @param hands A list of poses to render.
   */
  drawResultsPose(pose, state) {
    if (pose.keypoints != null) {
      this.drawKeypointsPose(pose.keypoints, state);
      this.drawSkeleton(pose.keypoints, pose.id, state);
    }
    // if (pose.keypoints3D != null && state.modelConfig.render3D) {
    //   this.drawKeypoints3D(pose.keypoints3D);
    // }
  }

  drawPathFaces(ctx, points, closePath) {
    const region = new Path2D();
    region.moveTo(points[0][0], points[0][1]);
    for (let i = 1; i < points.length; i += 1) {
      const point = points[i];
      region.lineTo(point[0], point[1]);
    }

    if (closePath) {
      region.closePath();
    }
    ctx.stroke(region);
  }

  distance(a, b) {
    return Math.sqrt(Math.pow(a[0] - b[0], 2) + Math.pow(a[1] - b[1], 2));
  }

  drawResultsFaces(faces, triangulateMesh, boundingBox) {
    // console.log('==============drawResultsFaces', faces, boundingBox, showKeypoints);
    faces.forEach((face) => {
      const keypoints = face.keypoints.map((keypoint) => [keypoint.x, keypoint.y]);

      if (boundingBox) {
        this.ctx.strokeStyle = RED;
        this.ctx.lineWidth = 1;

        const box = face.box;
        this.drawPathFaces(
          this.ctx,
          [
            [box.xMin, box.yMin], [box.xMax, box.yMin], [box.xMax, box.yMax],
            [box.xMin, box.yMax],
          ],
          true,
        );
      }

      if (triangulateMesh) {
        this.ctx.strokeStyle = GREEN;
        this.ctx.lineWidth = 0.5;

        for (let i = 0; i < TRIANGULATION.length / 3; i += 1) {
          const points = [
            TRIANGULATION[i * 3],
            TRIANGULATION[i * 3 + 1],
            TRIANGULATION[i * 3 + 2],
          ].map((index) => keypoints[index]);

          this.drawPathFaces(this.ctx, points, true);
        }
      } else {
        this.ctx.fillStyle = GREEN;

        for (let i = 0; i < NUM_KEYPOINTS; i += 1) {
          const x = keypoints[i][0];
          const y = keypoints[i][1];

          this.ctx.beginPath();
          this.ctx.arc(x, y, 1 /* radius */, 0, 2 * Math.PI);
          this.ctx.fill();
        }
      }

      if (keypoints.length > NUM_KEYPOINTS) {
        this.ctx.strokeStyle = RED;
        this.ctx.lineWidth = 1;

        const leftCenter = keypoints[NUM_KEYPOINTS];
        const leftDiameterY = this.distance(keypoints[NUM_KEYPOINTS + 4], keypoints[NUM_KEYPOINTS + 2]);
        const leftDiameterX = this.distance(keypoints[NUM_KEYPOINTS + 3], keypoints[NUM_KEYPOINTS + 1]);

        this.ctx.beginPath();
        this.ctx.ellipse(
          leftCenter[0], leftCenter[1], leftDiameterX / 2, leftDiameterY / 2, 0,
          0, 2 * Math.PI,
        );
        this.ctx.stroke();

        if (keypoints.length > NUM_KEYPOINTS + NUM_IRIS_KEYPOINTS) {
          const rightCenter = keypoints[NUM_KEYPOINTS + NUM_IRIS_KEYPOINTS];
          const rightDiameterY = this.distance(
            keypoints[NUM_KEYPOINTS + NUM_IRIS_KEYPOINTS + 2],
            keypoints[NUM_KEYPOINTS + NUM_IRIS_KEYPOINTS + 4],
          );
          const rightDiameterX = this.distance(
            keypoints[NUM_KEYPOINTS + NUM_IRIS_KEYPOINTS + 3],
            keypoints[NUM_KEYPOINTS + NUM_IRIS_KEYPOINTS + 1],
          );

          this.ctx.beginPath();
          this.ctx.ellipse(
            rightCenter[0], rightCenter[1], rightDiameterX / 2,
            rightDiameterY / 2, 0, 0, 2 * Math.PI,
          );
          this.ctx.stroke();
        }
      }

      //   const contours = faceLandmarksDetection.util.getKeypointIndexByContour(
      //     faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh,
      //   );

      //   Object.entries(contours).forEach(([label, contour]) => {
      //     this.ctx.strokeStyle = LABEL_TO_COLOR[label];
      //     this.ctx.lineWidth = 3;
      //     const path = contour.map((index) => keypoints[index]);
      //     if (path.every(value => value !== undefined)) {
      //       this.drawPathFaces(this.ctx, path, false);
      //     }
      //   });
    });
  }
}
